import React, {ReactElement, useEffect, useState} from 'react';

import Col from '../../../common/Col';
import Row from '../../../common/Row';
import {isNullish} from '../../../common/util';
import {useFctCalculator} from '../../decision/fctCalculator';
import {
  CURRENT_SCENARIO_IDS,
  CurrentScenarioId,
  SCENARIO_NAMES,
  TargetScenarioId,
} from '../../decision/fctCalculator.types';
import Select from '../Select';
import EmissionsChart from './EmissionsChart';
import EmissionsSavingBox from './EmissionsSavingBox';
import SimpleCalculatorCard from './SimpleCalculatorCard';

type Props = {};

const currentOptions = CURRENT_SCENARIO_IDS.map(value => ({
  label: SCENARIO_NAMES[value],
  value: value,
}));

const arableScenarios: typeof CURRENT_SCENARIO_IDS[number][] = [
  'arable_3yr_baseline',
  'arable_5yr_baseline',
  'arable_3yr_hemp',
  'arable_5yr_hemp',
];

const targetOptions = [
  {label: 'Miscanthus', value: 'miscanthus'},
  {label: 'SRC Willow', value: 'SRC_willow'},
  {label: 'SRC Poplar', value: 'SRC_poplar'},
  {label: 'Reed canary grass', value: 'reedcanary'},
  {label: 'Switchgrass', value: 'switchgrass'},
];

const FCTCalculator = ({}: Props): ReactElement => {
  const [currentScenarioId, setCurrentScenarioId] = useState<CurrentScenarioId>();
  const [targetScenarioId, setTargetScenarioId] = useState<TargetScenarioId>();
  const [targetScenarioOption, setTargetScenarioOption] = useState<string>();

  useEffect(() => {
    if (currentScenarioId && targetScenarioOption) {
      const isArable = arableScenarios.includes(currentScenarioId);
      switch (targetScenarioOption) {
        case 'miscanthus':
          setTargetScenarioId(isArable ? 'biomass_miscanthus_arable' : 'biomass_miscanthus_grassland');
          break;
        case 'SRC_willow':
          setTargetScenarioId(isArable ? 'biomass_SRC_willow_arable' : 'biomass_SRC_willow_grassland');
          break;
        case 'SRC_poplar':
          setTargetScenarioId(isArable ? 'biomass_SRC_poplar_arable' : 'biomass_SRC_poplar_grassland');
          break;
        case 'reedcanary':
          setTargetScenarioId(isArable ? 'biomass_reedcanary_arable' : 'biomass_reedcanary_grassland');
          break;
        case 'switchgrass':
          setTargetScenarioId(isArable ? 'biomass_switchgrass_arable' : 'biomass_switchgrass_grassland');
          break;
      }
    }
  }, [currentScenarioId, targetScenarioOption]);

  const input = {currentScenario: currentScenarioId, targetScenario: targetScenarioId};

  const currentLabel = currentScenarioId ? SCENARIO_NAMES[currentScenarioId] : '-';
  const targetLabel = targetScenarioOption
    ? targetOptions.find(option => option.value === targetScenarioOption)!.label
    : '-';

  const {data, isLoading, error} = useFctCalculator(input);

  const currentEmissions = data?.currentEmissions;
  const targetEmissions = data?.targetEmissions;

  const savings =
    isNullish(currentEmissions) || isNullish(targetEmissions) ? undefined : currentEmissions - targetEmissions;

  return (
    <SimpleCalculatorCard title={'Comparison of carbon footprint'}>
      <Row className={'w-full gap-md mobile:flex-col'}>
        <Col className={'grow basis-0 gap-3'}>
          <span className={'text-sm font-semibold'}>Select the closest match to your current system:</span>
          <Select
            floating
            buttonContent={currentLabel}
            options={currentOptions}
            selectedValues={currentScenarioId ? [currentScenarioId] : []}
            onOptionSelected={setCurrentScenarioId}
          />
        </Col>
        <Col className={'grow basis-0 gap-3'}>
          <span className={'text-sm font-semibold'}>Select a biomass crop to compare:</span>
          <Select
            floating
            buttonContent={targetLabel}
            options={targetOptions}
            selectedValues={targetScenarioOption ? [targetScenarioOption] : []}
            onOptionSelected={setTargetScenarioOption}
          />
        </Col>
      </Row>
      {!!error && <p className={'text-sm text-red-200'}>{String(error)}</p>}
      {currentScenarioId && targetScenarioId && !error && (
        <Col className={'items-center gap-md'}>
          <EmissionsChart
            leftValue={data?.currentEmissions || 0}
            rightValue={data?.targetEmissions || 0}
            leftLabel={currentLabel}
            rightLabel={targetLabel}
            isLoading={isLoading}
          />
          <EmissionsSavingBox savings={savings} />
        </Col>
      )}
    </SimpleCalculatorCard>
  );
};

export default FCTCalculator;
